import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, Users, Download, Filter } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/components/AuthProvider';
import { Badge } from '@/components/ui/badge';
import { collection, getDocs, query, where, orderBy, limit, getCountFromServer, getAggregateFromServer, sum, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useImpersonation } from '@/hooks/useImpersonation';
import { toast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Skeleton } from '@/components/ui/skeleton';

interface CompanyData {
  id: string;
  name: string;
  email: string;
  status: string;
  createdAt: Date;
  ownerId: string;
  ownerName?: string;
  ownerEmail?: string;
  ownerRole?: string;
  isSuperAdmin: boolean;
  userCount: number;
  invoiceCount: number;
  transactionCount: number;
  webformTotal: number;
  revenueTotal: number;
  monthsAsCustomer: number;
}

const TenantCustomers: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { startImpersonation } = useImpersonation();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [hasMorePages, setHasMorePages] = useState(false);

  // Check for super_admin role
  useEffect(() => {
    if (user) {
      if (user.role === 'super_admin') {
        setIsAuthorized(true);
      } else {
        navigate('/app');
      }
    }
  }, [user, navigate]);

  // Reset to page 1 when search query, status filter, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, sortBy]);

  // Fetch tenants data
  const { data: tenants, isLoading } = useQuery({
    queryKey: ['tenants', statusFilter, sortBy, searchQuery, currentPage],
    queryFn: fetchTenantsData,
    enabled: isAuthorized,
  });

  // Fetch tenants data function
  async function fetchTenantsData() {
    if (!user || user.role !== 'super_admin') {
      return [];
    }

    // Build query with proper constraint ordering
    const constraints = [];

    // Add status filter if not "all"
    if (statusFilter !== 'all') {
      constraints.push(where('status', '==', statusFilter));
    }

    // Add ordering
    constraints.push(
      orderBy(sortBy === 'name' ? 'name' : 'createdAt', sortBy === 'oldest' ? 'asc' : 'desc')
    );

    const tenantsQuery = query(collection(db, 'companies'), ...constraints);
    const querySnapshot = await getDocs(tenantsQuery);

    // Filter by search query client-side
    const allFilteredDocs = querySnapshot.docs.filter(doc => {
      const data = doc.data();
      if (searchQuery && !data.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      return true;
    });

    // Client-side pagination
    const itemsPerPage = 10;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const pagatedDocs = allFilteredDocs.slice(startIndex, endIndex + 1); // Fetch 11 to check if there are more

    // Check if there are more pages
    const hasMore = pagatedDocs.length > itemsPerPage;
    setHasMorePages(hasMore);

    // Limit to 10 items for display
    const filteredDocs = pagatedDocs.slice(0, itemsPerPage);

    // Process all tenants in parallel for much faster loading
    const tenantsDataPromises = filteredDocs.map(async (companyDoc) => {
      const data = companyDoc.data();
      const companyId = companyDoc.id;
      const createdAt = data.createdAt?.toDate() || new Date();

      // Calculate months as customer
      const now = new Date();
      const monthsDiff = Math.floor(
        (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24 * 30.44)
      );
      const monthsAsCustomer = Math.max(1, monthsDiff);

      // Run all queries for this tenant in parallel
      const [
        userCountSnapshot,
        invoiceCountSnapshot,
        transactionCountSnapshot,
        transactionsSnapshot,
        donationsSnapshot
      ] = await Promise.all([
        getCountFromServer(query(collection(db, 'users'), where('companyId', '==', companyId))),
        getCountFromServer(query(collection(db, 'invoices'), where('companyId', '==', companyId))),
        getCountFromServer(query(collection(db, 'transactions'), where('companyId', '==', companyId))),
        getDocs(query(collection(db, 'transactions'), where('companyId', '==', companyId))),
        getDocs(query(collection(db, 'donations'), where('companyId', '==', companyId)))
      ]);

      const userCount = userCountSnapshot.data().count;
      const invoiceCount = invoiceCountSnapshot.data().count;
      const transactionCount = transactionCountSnapshot.data().count;

      // Calculate revenue from paid transactions
      let revenueTotal = 0;
      transactionsSnapshot.docs.forEach(txDoc => {
        const txData = txDoc.data();
        if (txData.status === 'paid' || txData.status === 'completed') {
          revenueTotal += txData.amount || 0;
        }
      });

      // Calculate webform total from paid donations
      let webformTotal = 0;
      donationsSnapshot.docs.forEach(donDoc => {
        const donData = donDoc.data();
        if (donData.status === 'paid' || donData.status === 'completed') {
          webformTotal += donData.amount || 0;
        }
      });

      // Fetch owner's role to determine if they're a super_admin
      let ownerRole = '';
      let isSuperAdmin = false;
      if (data.ownerId) {
        try {
          const ownerDoc = await getDoc(doc(db, 'users', data.ownerId));
          if (ownerDoc.exists()) {
            const ownerData = ownerDoc.data();
            ownerRole = ownerData.role || '';
            isSuperAdmin = ownerRole === 'super_admin';
          }
        } catch (error) {
          console.error('Error fetching owner role:', error);
        }
      }

      return {
        id: companyDoc.id,
        name: data.name || 'Unnamed Tenant',
        email: data.email || 'No Email',
        status: data.status || 'inactive',
        createdAt: createdAt,
        ownerId: data.ownerId || '',
        ownerName: data.ownerName || 'No Owner',
        ownerEmail: data.ownerEmail || '',
        ownerRole,
        isSuperAdmin,
        userCount,
        invoiceCount,
        transactionCount,
        webformTotal,
        revenueTotal,
        monthsAsCustomer,
      };
    });

    const tenantsData = await Promise.all(tenantsDataPromises);
    return tenantsData;
  }

  const handleViewTenant = (tenantId: string) => {
    navigate(`/app/tenants/${tenantId}`);
  };

  const handleLoginAsAdmin = async (e: React.MouseEvent, companyId: string, companyName: string, isSuperAdmin: boolean) => {
    e.stopPropagation();

    // Prevent impersonating super_admin companies
    if (isSuperAdmin) {
      toast({
        title: 'Cannot Impersonate',
        description: 'Super admin companies cannot be impersonated',
        variant: 'destructive',
      });
      return;
    }

    if (isImpersonating) {
      toast({
        title: 'Action in progress',
        description: 'Please wait for the previous action to complete',
        variant: 'default',
      });
      return;
    }

    setIsImpersonating(true);

    try {
      const result = await startImpersonation(companyId);

      if (!result.success) {
        toast({
          title: 'Impersonation failed',
          description: result.error || 'Failed to start impersonation session',
          variant: 'destructive',
        });
        setIsImpersonating(false);
      }
      // Success case will redirect automatically in the hook
    } catch (error) {
      console.error('[handleLoginAsAdmin] Error:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
      setIsImpersonating(false);
    }
  };

  const handleMakeSuperAdmin = async (e: React.MouseEvent, companyId: string, companyName: string, ownerId?: string) => {
    e.stopPropagation();

    try {
      let userToPromote = null;
      let userRef = null;

      // Try to find user by ownerId first if provided
      if (ownerId) {
        userRef = doc(db, 'users', ownerId);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data() as Record<string, any>;
          userToPromote = { id: ownerId, ...userData };
        }
      }

      // If no owner found, find any admin user for this company
      if (!userToPromote) {
        const usersQuery = query(
          collection(db, 'users'),
          where('companyId', '==', companyId),
          where('role', 'in', ['admin', 'owner']),
          limit(1)
        );
        const usersSnapshot = await getDocs(usersQuery);

        if (!usersSnapshot.empty) {
          const userDoc = usersSnapshot.docs[0];
          const userData = userDoc.data() as Record<string, any>;
          userToPromote = { id: userDoc.id, ...userData };
          userRef = doc(db, 'users', userDoc.id);
        }
      }

      if (!userToPromote || !userRef) {
        toast({
          title: 'Error',
          description: `No admin user found for ${companyName}. An admin user must log in first.`,
          variant: 'destructive',
        });
        return;
      }

      // Update the user's role to super_admin
      await updateDoc(userRef, {
        role: 'super_admin'
      });

      toast({
        title: 'Success',
        description: `${userToPromote.email || companyName} has been granted super_admin rights`,
        variant: 'default',
      });

      // Refresh the tenants list
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    } catch (error) {
      console.error('[handleMakeSuperAdmin] Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to grant super_admin rights',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveSuperAdmin = async (e: React.MouseEvent, ownerId: string, ownerName: string) => {
    e.stopPropagation();

    try {
      // First verify the user exists
      const userRef = doc(db, 'users', ownerId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        toast({
          title: 'Error',
          description: `User document not found for ${ownerName}`,
          variant: 'destructive',
        });
        return;
      }

      // Update the owner's role to admin
      await updateDoc(userRef, {
        role: 'admin'
      });

      toast({
        title: 'Success',
        description: `Super_admin rights have been removed from ${ownerName}`,
        variant: 'default',
      });

      // Refresh the tenants list
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    } catch (error) {
      console.error('[handleRemoveSuperAdmin] Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove super_admin rights',
        variant: 'destructive',
      });
    }
  };

  // If not super_admin, don't render the page
  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="container mx-auto py-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Tenant Management</h1>
          <p className="text-muted-foreground">
            View and manage all your tenant companies
          </p>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search tenants..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        
        <div className="flex items-center gap-2 ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <span>Sort By</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSortBy('name')}>
                Company Name (A-Z)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy('newest')}>
                Newest First
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy('oldest')}>
                Oldest First
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button variant="outline" size="sm" disabled>
            <Download className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Export</span>
          </Button>
        </div>
      </div>
        
      {/* Customer Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Invoices</TableHead>
                <TableHead>Transactions</TableHead>
                <TableHead>Webform $$$</TableHead>
                <TableHead>Total Revenue</TableHead>
                <TableHead>Months</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                Array(5).fill(0).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-12" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-12" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-12" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-12" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                  </TableRow>
                ))
              )}
              
              {!isLoading && tenants?.map(tenant => (
                <TableRow
                  key={tenant.id}
                  className={`cursor-pointer hover:bg-muted/50 ${tenant.isSuperAdmin ? 'text-red-600' : ''}`}
                  onClick={() => handleViewTenant(tenant.id)}
                >
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tenant.isSuperAdmin ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                        {tenant.name.charAt(0)}
                      </div>
                      <div>
                        <div className={`font-medium ${tenant.isSuperAdmin ? 'text-red-600' : ''}`}>{tenant.name}</div>
                        <div className={`text-sm ${tenant.isSuperAdmin ? 'text-red-500' : 'text-muted-foreground'}`}>{tenant.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge variant={tenant.status === 'active' ? 'success' : 'secondary'}>
                        {tenant.status}
                      </Badge>
                      {tenant.isSuperAdmin && (
                        <Badge variant="destructive" className="text-xs">
                          SUPER ADMIN
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className={tenant.isSuperAdmin ? 'text-red-600' : ''}>{tenant.userCount}</TableCell>
                  <TableCell className={tenant.isSuperAdmin ? 'text-red-600' : ''}>{tenant.invoiceCount}</TableCell>
                  <TableCell className={tenant.isSuperAdmin ? 'text-red-600' : ''}>{tenant.transactionCount}</TableCell>
                  <TableCell className={tenant.isSuperAdmin ? 'text-red-600' : ''}>${tenant.webformTotal.toLocaleString()}</TableCell>
                  <TableCell className={tenant.isSuperAdmin ? 'text-red-600' : ''}>${tenant.revenueTotal.toLocaleString()}</TableCell>
                  <TableCell className={tenant.isSuperAdmin ? 'text-red-600' : ''}>{tenant.monthsAsCustomer} {tenant.monthsAsCustomer === 1 ? 'month' : 'months'}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="sm">
                          Actions
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          handleViewTenant(tenant.id);
                        }}>
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => handleLoginAsAdmin(e, tenant.id, tenant.name, tenant.isSuperAdmin)}
                          disabled={isImpersonating || tenant.isSuperAdmin}
                        >
                          {isImpersonating ? 'Loading...' : tenant.isSuperAdmin ? 'Cannot Impersonate Super Admin' : 'Login as Admin'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {!tenant.isSuperAdmin && (
                          <DropdownMenuItem
                            onClick={(e) => handleMakeSuperAdmin(e, tenant.id, tenant.name, tenant.ownerId)}
                          >
                            Make Super Admin
                          </DropdownMenuItem>
                        )}
                        {tenant.isSuperAdmin && tenant.ownerId && (
                          <DropdownMenuItem
                            className="text-orange-600"
                            onClick={(e) => handleRemoveSuperAdmin(e, tenant.ownerId, tenant.ownerName || tenant.name)}
                          >
                            Remove Super Admin
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className={tenant.status === 'active' ? 'text-destructive' : 'text-green-600'}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Toggle tenant status
                          }}
                        >
                          {tenant.status === 'active' ? 'Deactivate' : 'Activate'} Tenant
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              
              {!isLoading && tenants?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="flex flex-col items-center justify-center text-muted-foreground">
                      <Building2 className="h-12 w-12 mb-2" />
                      <h3 className="text-lg font-medium">No tenants found</h3>
                      <p className="text-sm">
                        {searchQuery
                          ? `No results for "${searchQuery}"`
                          : statusFilter !== 'all'
                            ? `No ${statusFilter} tenants found`
                            : 'Start by creating your first tenant'
                        }
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Pagination */}
      {(tenants && tenants.length > 0) || currentPage > 1 ? (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage > 1) setCurrentPage(currentPage - 1);
                }}
                className={currentPage <= 1 ? 'pointer-events-none opacity-50' : ''}
              />
            </PaginationItem>

            {/* Page numbers */}
            {currentPage > 1 && (
              <PaginationItem>
                <PaginationLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage(currentPage - 1);
                  }}
                >
                  {currentPage - 1}
                </PaginationLink>
              </PaginationItem>
            )}

            <PaginationItem>
              <PaginationLink href="#" isActive>{currentPage}</PaginationLink>
            </PaginationItem>

            {hasMorePages && (
              <PaginationItem>
                <PaginationLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage(currentPage + 1);
                  }}
                >
                  {currentPage + 1}
                </PaginationLink>
              </PaginationItem>
            )}

            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (hasMorePages) setCurrentPage(currentPage + 1);
                }}
                className={!hasMorePages ? 'pointer-events-none opacity-50' : ''}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      ) : null}
    </div>
  );
};

export default TenantCustomers; 